// Caja de recepción (script 939): turno por usuario con fondo, movimientos
// (depósitos, cobros de check-out, devoluciones) y cierre con arqueo; la
// diferencia se explica y se contabiliza como faltante/sobrante.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, money, hotelRpc, QK, fmtFecha, hoyISO, sumarDias, useDialogoEnLinea } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Turno {
  id: string; codigo: string; usuario: string | null; abierto_at: string; fondo: number; estado: string; exigir?: boolean;
  efectivo_esperado: number; por_forma: Record<string, number>;
  movimientos: { tipo: string; forma: string; monto: number; referencia: string | null; reserva: string | null; hora: string }[];
}
interface TurnoFila { id: string; codigo: string; usuario: string | null; abierto_at: string; cerrado_at: string | null; estado: string; fondo: number;
  esperado: number | null; contado: number | null; diferencia: number | null; observacion: string | null; efectivo: number; otros: number }
const TIPO: Record<string, string> = { deposito: 'Depósito', cobro: 'Cobro check-out', devolucion: 'Devolución' };
const hora = (iso: string) => new Date(iso).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });

export function CajaRecepcion() {
  const dlg = useDialogoEnLinea();
  const qc = useQueryClient();
  const { data: t, isLoading } = useQuery<Turno | null>({ queryKey: [QK, 'turno'], queryFn: () => hotelRpc<Turno | null>('turno_actual', {}), refetchInterval: 30000 });
  const [fondo, setFondo] = useState(0);
  const [contado, setContado] = useState('');
  const [obs, setObs] = useState('');
  const invalidar = () => void qc.invalidateQueries({ queryKey: [QK] });
  const abrir = useMutation({
    mutationFn: () => hotelRpc<{ codigo: string }>('turno_abrir', { _fondo: fondo }),
    onSuccess: (r) => { toast.success(`Turno ${r.codigo} abierto`); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: () => hotelRpc<{ diferencia: number }>('turno_cerrar', { _contado: Number(contado), _observacion: obs.trim() || null }),
    onSuccess: (r) => { toast.success(Number(r.diferencia) === 0 ? 'Turno cerrado: la caja cuadra' : `Turno cerrado con diferencia de ${money(Number(r.diferencia))}`); setContado(''); setObs(''); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const dif = contado === '' || !t ? null : Number(contado) - Number(t.efectivo_esperado);

  return (
    <div className="space-y-4 p-6">
      {dlg.ui}
      <h1 className="flex items-center gap-2 text-xl font-semibold"><Wallet className="h-5 w-5" /> Caja de recepción</h1>
      {isLoading ? <p className="text-sm text-muted-foreground">Cargando…</p> : !t ? (
        <Card className="max-w-md">
          <CardHeader className="pb-2"><CardTitle className="text-base">Abrir turno</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">Los depósitos, cobros de check-out y devoluciones que haga quedarán en su turno, por forma de pago.</p>
            <Campo label="Fondo inicial en efectivo"><input type="number" min={0} step="0.01" className={fieldCls} value={fondo} onChange={(e) => setFondo(Number(e.target.value) || 0)} /></Campo>
            <Button isLoading={abrir.isPending} onClick={() => abrir.mutate()}>Abrir turno</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2"><CardTitle className="text-base">Turno {t.codigo} · {t.usuario} · desde {hora(t.abierto_at)}</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-2">N°</th><th className="py-1.5 pr-2">Hora</th><th className="py-1.5 pr-2">Movimiento</th><th className="py-1.5 pr-2">Reserva</th>
                  <th className="py-1.5 pr-2">Forma</th><th className="py-1.5 pr-2">Referencia</th><th className="py-1.5 pr-2 text-right">Monto</th>
                </tr></thead>
                <tbody>
                  {t.movimientos.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin movimientos todavía.</td></tr>}
                  {t.movimientos.map((m, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                      <td className="py-1 pr-2">{hora(m.hora)}</td>
                      <td className="py-1 pr-2">{TIPO[m.tipo] ?? m.tipo}</td>
                      <td className="py-1 pr-2 font-mono">{m.reserva ?? '—'}</td>
                      <td className="py-1 pr-2 capitalize">{m.forma}</td>
                      <td className="py-1 pr-2 text-muted-foreground">{m.referencia ?? ''}</td>
                      <td className={`py-1 pr-2 text-right font-mono tabular-nums ${Number(m.monto) < 0 ? 'text-red-600' : ''}`}>{money(Number(m.monto))}</td>
                    </tr>
                  ))}
                </tbody>
                {t.movimientos.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
                  <td colSpan={6} className="py-1.5">Total del turno</td>
                  <td className="py-1.5 pr-2 text-right font-mono tabular-nums">{money(t.movimientos.reduce((a, m) => a + Number(m.monto), 0))}</td>
                </tr></tfoot>}
              </table>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Arqueo y cierre</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Fondo</span><span className="font-mono">{money(Number(t.fondo))}</span></div>
              {Object.entries(t.por_forma).map(([f, v]) => <div key={f} className="flex justify-between capitalize"><span>{f}</span><span className="font-mono">{money(Number(v))}</span></div>)}
              <div className="flex justify-between border-t pt-1 font-semibold"><span>Efectivo esperado</span><span className="font-mono">{money(Number(t.efectivo_esperado))}</span></div>
              <Campo label="Efectivo contado"><input type="number" min={0} step="0.01" className={fieldCls} value={contado} onChange={(e) => setContado(e.target.value)} /></Campo>
              {dif != null && <p className={`rounded px-2 py-1 text-xs ${Math.abs(dif) < 0.005 ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300'}`}>
                {Math.abs(dif) < 0.005 ? 'La caja cuadra.' : `${dif < 0 ? 'Faltante' : 'Sobrante'} de ${money(Math.abs(dif))}: explíquelo antes de cerrar.`}</p>}
              <Campo label="Observación"><input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)} /></Campo>
              <Button className="w-full" isLoading={cerrar.isPending} disabled={contado === '' || (dif != null && Math.abs(dif) >= 0.005 && obs.trim().length < 5)}
                onClick={async () => { if (await dlg.confirmar('¿Cerrar el turno?', { detalle: 'Ya no podrá registrar movimientos en él.', aceptar: 'Cerrar turno' })) cerrar.mutate(); }}>Cerrar turno</Button>
              {t.exigir && <p className="text-xs text-muted-foreground">El hotel exige turno abierto para recibir o devolver efectivo.</p>}
            </CardContent>
          </Card>
        </div>
      )}
      <HistorialTurnos />
    </div>
  );
}

function HistorialTurnos() {
  const [desde, setDesde] = useState(sumarDias(hoyISO(), -7));
  const [hasta, setHasta] = useState(hoyISO());
  const { data = [] } = useQuery<TurnoFila[]>({ queryKey: [QK, 'turnos', desde, hasta], queryFn: () => hotelRpc<TurnoFila[]>('turnos_listar', { _desde: desde, _hasta: hasta }) });
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-end justify-between gap-2 pb-2">
        <CardTitle className="text-base">Turnos</CardTitle>
        <div className="flex gap-2">
          <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-1.5 pr-2">N°</th><th className="py-1.5 pr-2">Turno</th><th className="py-1.5 pr-2">Usuario</th><th className="py-1.5 pr-2">Apertura</th>
            <th className="py-1.5 pr-2">Estado</th><th className="py-1.5 pr-2 text-right">Efectivo</th><th className="py-1.5 pr-2 text-right">Otras formas</th>
            <th className="py-1.5 pr-2 text-right">Esperado</th><th className="py-1.5 pr-2 text-right">Contado</th><th className="py-1.5 pr-2 text-right">Diferencia</th><th className="py-1.5 pr-2">Observación</th>
          </tr></thead>
          <tbody>
            {data.length === 0 && <tr><td colSpan={11} className="py-6 text-center text-muted-foreground">Sin turnos en el rango.</td></tr>}
            {data.map((x, i) => (
              <tr key={x.id} className="border-b last:border-0">
                <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                <td className="py-1 pr-2 font-mono">{x.codigo}</td>
                <td className="py-1 pr-2">{x.usuario}</td>
                <td className="py-1 pr-2 whitespace-nowrap">{fmtFecha(x.abierto_at)} {hora(x.abierto_at)}</td>
                <td className="py-1 pr-2 capitalize">{x.estado}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{money(Number(x.efectivo))}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{money(Number(x.otros))}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{x.esperado != null ? money(Number(x.esperado)) : '—'}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{x.contado != null ? money(Number(x.contado)) : '—'}</td>
                <td className={`py-1 pr-2 text-right tabular-nums ${Number(x.diferencia ?? 0) !== 0 ? 'font-semibold text-red-600' : ''}`}>{x.diferencia != null ? money(Number(x.diferencia)) : '—'}</td>
                <td className="py-1 pr-2 text-muted-foreground">{x.observacion ?? ''}</td>
              </tr>
            ))}
          </tbody>
          {data.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
            <td colSpan={5} className="py-1.5">Totales ({data.length})</td>
            <td className="py-1.5 pr-2 text-right tabular-nums">{money(data.reduce((a, x) => a + Number(x.efectivo), 0))}</td>
            <td className="py-1.5 pr-2 text-right tabular-nums">{money(data.reduce((a, x) => a + Number(x.otros), 0))}</td>
            <td colSpan={2} />
            <td className="py-1.5 pr-2 text-right tabular-nums">{money(data.reduce((a, x) => a + Number(x.diferencia ?? 0), 0))}</td>
            <td />
          </tr></tfoot>}
        </table>
      </CardContent>
    </Card>
  );
}
